import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoCheckmarkCircle,
  IoDocumentText,
  IoDownload,
  IoReloadOutline,
  IoAlertCircle,
  IoTimeOutline,
  IoLocationOutline,
  IoCallOutline,
  IoPersonOutline,
  IoBusinessOutline,
  IoCheckmarkDoneOutline,
  IoCalendarOutline,    
  IoMedkitOutline,        
  IoCubeOutline, 
} from "react-icons/io5";
import toast from "react-hot-toast";
import axiosAuth from "../../../utils/axios";

const PendingOrdersAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("pending"); // 'pending' or 'approved'
  const [orders, setOrders] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openingIvrOrderNumber, setOpeningIvrOrderNumber] = useState(null);
  const [approvingOrderId, setApprovingOrderId] = useState(null);
  const [disapprovingOrderId, setDisapprovingOrderId] = useState(null);
  const [notesMap, setNotesMap] = useState({});

  const totalPending = orders.length;

  const fetchOrders = async (tab, mode = "load") => {
    const isHardLoad = mode === "load";
    const isSoftRefresh = mode === "refresh";

    try {
      if (isHardLoad) setIsPageLoading(true);
      if (isSoftRefresh) setIsRefreshing(true);

      const axiosInstance = axiosAuth();
      const endpoint = tab === "pending" ? "pending-guided/" : "approved-guided/";
      const response = await axiosInstance.get(endpoint);

      if (response?.data?.success) {
        setOrders(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        toast.error("Failed to load orders");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      toast.error("Failed to load orders");
    } finally {
      setIsPageLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders(activeTab, "load");
  }, [activeTab]);

  const handleOpenIVR = async (ivrUrl, orderNumber) => {
    if (!ivrUrl) {
      toast.error("IVR PDF not available");
      return;
    }

    try {
      setOpeningIvrOrderNumber(orderNumber);
      window.open(ivrUrl, "_blank", "noopener,noreferrer");
      toast.success("Opening IVR PDF...");
    } catch (err) {
      console.error("Error opening IVR:", err);
      toast.error("Failed to open IVR");
    } finally {
      setOpeningIvrOrderNumber(null);
    }
  };

  const handleApproveIVR = async (orderId, orderNumber) => {
    const notes = notesMap[orderId]?.trim() || '';
    
    if (!notes) {
      toast.error('Medical specialist notes are required');
      return;
    }

    try {
      setApprovingOrderId(orderId);
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.post(
        `orders/${orderId}/approve-ivr-dashboard/`,
        { medical_specialist_notes: notes }
      );

      if (response?.data?.success) {
        toast.success(`✅ IVR approved for Order #${orderNumber}`);
        setNotesMap(prev => {
          const updated = { ...prev };
          delete updated[orderId];
          return updated;
        });
        fetchOrders(activeTab, "refresh");
      } else {
        toast.error(response?.data?.error || "Failed to approve IVR");
      }
    } catch (err) {
      console.error("Error approving IVR:", err);
      toast.error(err?.response?.data?.error || "Failed to approve IVR");
    } finally {
      setApprovingOrderId(null);
    }
  };

  const handleDisapproveIVR = async (orderId, orderNumber) => {
    const notes = notesMap[orderId]?.trim() || '';
    
    if (!notes) {
      toast.error('Medical specialist notes are required to explain disapproval');
      return;
    }

    if (!window.confirm(
      `Are you sure you want to disapprove IVR for Order #${orderNumber}?\n\n` +
      `The provider and sales rep will be notified to resubmit the IVR form.`
    )) {
      return;
    }

    try {
      setDisapprovingOrderId(orderId);
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.post(
        `orders/${orderId}/disapprove-ivr-dashboard/`,
        { medical_specialist_notes: notes }
      );

      if (response?.data?.success) {
        toast.success(`❌ IVR disapproved for Order #${orderNumber}. Notifications sent.`);
        setNotesMap(prev => {
          const updated = { ...prev };
          delete updated[orderId];
          return updated;
        });
        fetchOrders(activeTab, "refresh");
      } else {
        toast.error(response?.data?.error || "Failed to disapprove IVR");
      }
    } catch (err) {
      console.error("Error disapproving IVR:", err);
      toast.error(err?.response?.data?.error || "Failed to disapprove IVR");
    } finally {
      setDisapprovingOrderId(null);
    }
  };

  const handleNotesChange = (orderId, value) => {
    setNotesMap(prev => ({
      ...prev,
      [orderId]: value
    }));
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      return new Date(dateValue).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const statusUi = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "approved") {
      return {
        label: "Approved",
        pill: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        icon: <IoCheckmarkCircle className="text-emerald-600" />,
        dot: "bg-emerald-500",
      };
    }
    if (normalized === "rejected" || normalized === "declined") {
      return {
        label: status || "Rejected",
        pill: "bg-rose-50 text-rose-700 ring-rose-200",
        icon: <IoAlertCircle className="text-rose-600" />,
        dot: "bg-rose-500",
      };
    }
    return {
      label: status || "Pending",
      pill: "bg-amber-50 text-amber-700 ring-amber-200",
      icon: <IoTimeOutline className="text-amber-600" />,
      dot: "bg-amber-500",
    };
  };

  const headerSubtitle = useMemo(() => {
    if (activeTab === "pending") {
      if (totalPending === 0) return "No pending guided orders right now.";
      if (totalPending === 1) return "1 order needs review.";
      return `${totalPending} orders need review.`;
    } else {
      if (totalPending === 0) return "No approved orders yet.";
      if (totalPending === 1) return "1 approved order.";
      return `${totalPending} approved orders.`;
    }
  }, [totalPending, activeTab]);

  if (isPageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30">
        <div className="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-6">
          <div className="rounded-2xl border border-teal-100 bg-white/70 p-8 shadow-sm backdrop-blur">
            <div className="flex items-center gap-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="h-10 w-10 rounded-full border-4 border-teal-100 border-t-teal-600"
              />
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Loading orders
                </p>
                <p className="text-sm text-slate-600">
                  Fetching the latest guided orders...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30">
      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Top Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/70 px-3 py-1 text-xs font-semibold text-teal-700 shadow-sm backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-teal-500" />
                Admin Console • Guided Orders
              </div>

              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {activeTab === "pending" ? "Pending" : "Approved"} Guided Orders
              </h1>

              <p className="mt-2 text-sm text-slate-600 sm:text-base">
                {activeTab === "pending" 
                  ? "Review pending orders and download IVR forms." 
                  : "View all approved orders with IVR documentation."}{" "}
                <span className="font-semibold text-slate-700">
                  {headerSubtitle}
                </span>
              </p>
            </div>

            <button
              onClick={() => fetchOrders(activeTab, "refresh")}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
            >
              <motion.span
                animate={isRefreshing ? { rotate: 360 } : { rotate: 0 }}
                transition={
                  isRefreshing
                    ? { duration: 0.9, repeat: Infinity, ease: "linear" }
                    : { duration: 0.2 }
                }
                className="inline-flex"
              >
                <IoReloadOutline className="text-teal-600" />
              </motion.span>
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 p-1.5 rounded-2xl bg-slate-100 dark:bg-slate-900/50 w-fit">
            <button
              onClick={() => setActiveTab("pending")}
              className={`relative px-6 py-2.5 rounded-xl text-sm font-extrabold transition-all ${
                activeTab === "pending"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <IoTimeOutline className="text-lg" />
                Pending IVR
              </span>
            </button>
            <button
              onClick={() => setActiveTab("approved")}
              className={`relative px-6 py-2.5 rounded-xl text-sm font-extrabold transition-all ${
                activeTab === "approved"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <IoCheckmarkDoneOutline className="text-lg" />
                Approved IVR
              </span>
            </button>
          </div>

          {/* KPI / Stats */}
          <div className="mt-6 rounded-3xl border border-teal-100 bg-white/70 p-5 shadow-sm backdrop-blur">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500">
                  Total {activeTab === "pending" ? "Pending" : "Approved"} Orders
                </p>
                <p className="mt-2 text-4xl font-extrabold tracking-tight text-slate-900">
                  {totalPending}
                </p>
              </div>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100">
                {activeTab === "pending" ? (
                  <IoDocumentText className="text-xl text-teal-700" />
                ) : (
                  <IoCheckmarkDoneOutline className="text-xl text-teal-700" />
                )}
              </div>
            </div>

            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br from-teal-200/40 to-emerald-200/30 blur-2xl" />
          </div>
        </motion.div>

        {/* Content */}
        {orders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl border border-slate-200 bg-white/70 p-10 text-center shadow-sm backdrop-blur"
          >
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-50 ring-1 ring-slate-200">
              {activeTab === "pending" ? (
                <IoCheckmarkCircle className="text-3xl text-emerald-600" />
              ) : (
                <IoDocumentText className="text-3xl text-slate-400" />
              )}
            </div>
            <h3 className="mt-4 text-xl font-bold text-slate-900">
              {activeTab === "pending" ? "All caught up" : "No approved orders"}
            </h3>
            <p className="mt-2 text-slate-600">
              {activeTab === "pending"
                ? "No pending guided orders at the moment."
                : "No orders have been approved yet."}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {orders.map((order, index) => {
                const status = statusUi(order.ivr_status);
                const createdAt = formatDate(order.created_at);
                const updatedAt = formatDate(order.updated_at);

                return (
                  <motion.div
                    key={order.id ?? order.order_number ?? index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ delay: index * 0.03 }}
                    className="rounded-2xl border border-slate-200 bg-white/70 p-6 shadow-sm backdrop-blur transition hover:shadow-md"
                  >
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                      {/* LEFT: Order Details */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-teal-700 ring-1 ring-teal-200">
                            <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                            Guided Order
                          </span>

                          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 ${status.pill}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                            {status.label}
                          </span>

                          <span className="ml-0 inline-flex items-center gap-2 text-xs font-semibold text-slate-500 sm:ml-2">
                            <IoTimeOutline />
                            {activeTab === "approved" ? `Approved ${updatedAt}` : `Created ${createdAt}`}
                          </span>
                        </div>

                        <div className="mt-4 flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <h3 className="truncate text-2xl font-extrabold tracking-tight text-slate-900">
                              Order #{order.order_number}
                            </h3>
                            <p className="mt-1 text-sm text-slate-600">
                              {activeTab === "approved" 
                                ? "Approved order with IVR documentation."
                                : "Provider order review and IVR access."}
                            </p>
                          </div>
                        </div>

                        {/* info grid */}
                        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                          <InfoBlock
                            icon={<IoBusinessOutline />}
                            label="Provider"
                            value={order.provider_name || "N/A"}
                            subValue={order.provider_email || ""}
                          />

                          <InfoBlock
                            icon={<IoPersonOutline />}
                            label="Patient"
                            value={order.patient_name || "N/A"}
                          />

                          <InfoBlock
                            icon={<IoDocumentText />}
                            label="Care Type"
                            value={
                              order.care_type === "propacks"
                                ? "ProPacks (Conservative)"
                                : "Advanced Care"
                            }
                          />

                           <InfoBlock
                              icon={<IoCalendarOutline />}
                              label="Kit Duration"
                              value={order.kit_duration || "N/A"}
                            />

                          <InfoBlock
                            icon={<IoAlertCircle />}
                            label="Wound Type"
                            value={order.wound_type || "N/A"}
                          />

                          {order.wound_location && (
                            <InfoBlock
                              icon={<IoLocationOutline />}
                              label="Wound Location"
                              value={order.wound_location}
                            />
                          )}

                           {order.care_type === "propacks" && order.propack_config && (
    <div className="md:col-span-2">
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
            <IoCubeOutline className="text-lg" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-slate-500">ProPack Kit</p>
            <div className="mt-2 space-y-2">
              {order.propack_config.primary && (
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold text-slate-600">Primary</p>
                  <p className="text-sm font-bold text-slate-900">
                    {order.propack_config.primary.type?.replace(/_/g, ' ')?.toUpperCase() || "N/A"}
                    {order.propack_config.primary.size && ` - ${order.propack_config.primary.size}`}
                  </p>
                </div>
              )}
              {order.propack_config.secondary && (
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold text-slate-600">Secondary</p>
                  <p className="text-sm font-bold text-slate-900">
                    {order.propack_config.secondary.type?.replace(/_/g, ' ')?.toUpperCase() || "N/A"}
                    {order.propack_config.secondary.size && ` - ${order.propack_config.secondary.size}`}
                  </p>
                </div>
              )}
              {order.propack_config.default_items && order.propack_config.default_items.length > 0 && (
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-xs font-semibold text-slate-600">Default Items</p>
                  <p className="text-sm font-bold text-slate-900">
                    {order.propack_config.default_items.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )}

  {/* ✅ GRAFT DETAILS - Only show if care_type is advanced */}
  {order.care_type === "advanced" && (order.graft_name || order.graft_size) && (
    <>
      <InfoBlock
        icon={<IoMedkitOutline />}
        label="Graft Name"
        value={order.graft_name || "N/A"}
      />
      <InfoBlock
        icon={<IoCubeOutline />}
        label="Graft Size"
        value={order.graft_size || "N/A"}
      />
    </>
  )}

                          <InfoBlock
                            icon={<IoBusinessOutline />}
                            label="Facility"
                            value={order.facility_name || "N/A"}
                          />

                          <div className="md:col-span-2">
                            <InfoBlock
                              icon={<IoLocationOutline />}
                              label="Delivery Address"
                              value={`${order.street || ""}${
                                order.street ? ", " : ""
                              }${order.city || ""}${
                                order.city ? ", " : ""
                              }${order.state || ""} ${order.zip_code || ""}`.trim() || "N/A"}
                            />
                          </div>

                          <InfoBlock
                            icon={<IoCallOutline />}
                            label="Phone"
                            value={order.phone_number || "N/A"}
                          />
                          
                          {/* Show medical notes for approved orders */}
                          {activeTab === "approved" && order.medical_specialist_notes && (
                            <div className="md:col-span-2">
                              <InfoBlock
                                icon={<IoDocumentText />}
                                label="Medical Specialist Notes"
                                value={order.medical_specialist_notes}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* RIGHT: IVR Panel */}
                      {
                        activeTab !== "approved" && (
                           <div className="w-full lg:w-[310px]">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="grid h-9 w-9 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100">
                                <IoDocumentText className="text-lg text-teal-700" />
                              </div>
                              <div>
                                <p className="text-sm font-extrabold text-slate-900">
                                  IVR Form
                                </p>
                                <p className="text-xs text-slate-500">
                                  {activeTab === "approved" ? "Download" : "Status & download"}
                                </p>
                              </div>
                            </div>

                            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-extrabold ring-1 ${status.pill}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                              {status.label}
                            </span>
                          </div>

                          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                            {order.has_ivr ? (
                              <>
                                <div className="flex items-center gap-2">
                                  {status.icon}
                                  <p className="text-sm font-semibold text-slate-800">
                                    {status.label}
                                  </p>
                                </div>

                                <button
                                  onClick={() =>
                                    handleOpenIVR(order.ivr_download_url, order.order_number)
                                  }
                                  disabled={
                                    openingIvrOrderNumber === order.order_number ||
                                    !order.ivr_download_url
                                  }
                                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {openingIvrOrderNumber === order.order_number ? (
                                    <>
                                      <motion.span
                                        animate={{ rotate: 360 }}
                                        transition={{
                                          duration: 1,
                                          repeat: Infinity,
                                          ease: "linear",
                                        }}
                                        className="inline-flex"
                                      >
                                        <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" />
                                      </motion.span>
                                      Opening...
                                    </>
                                  ) : (
                                    <>
                                      <IoDownload className="text-lg" />
                                      Download IVR PDF
                                    </>
                                  )}
                                </button>

                                {/* Show approval/disapproval buttons only for pending tab */}
                                {activeTab === "pending" && order.ivr_status !== "Approved" && (
                                  <div className="mt-4 space-y-3">
                                    <div>
                                      <label 
                                        htmlFor={`notes-${order.id}`}
                                        className="block text-xs font-semibold text-slate-700 mb-2"
                                      >
                                        Medical Specialist Notes *
                                      </label>
                                      <textarea
                                        id={`notes-${order.id}`}
                                        value={notesMap[order.id] || ''}
                                        onChange={(e) => handleNotesChange(order.id, e.target.value)}
                                        placeholder="Enter medical specialist notes before approving/disapproving..."
                                        rows={4}
                                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                      />
                                    </div>

                                    <button
                                      onClick={() => handleApproveIVR(order.id, order.order_number)}
                                      disabled={
                                        approvingOrderId === order.id || 
                                        disapprovingOrderId === order.id ||
                                        !notesMap[order.id]?.trim()
                                      }
                                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-emerald-600 hover:to-green-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                      {approvingOrderId === order.id ? (
                                        <>
                                          <motion.span
                                            animate={{ rotate: 360 }}
                                            transition={{
                                              duration: 1,
                                              repeat: Infinity,
                                              ease: "linear",
                                            }}
                                            className="inline-flex"
                                          >
                                            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" />
                                          </motion.span>
                                          Approving...
                                        </>
                                      ) : (
                                        <>
                                          <IoCheckmarkCircle className="text-lg" />
                                          Approve IVR
                                        </>
                                      )}
                                    </button>

                                    <button
                                      onClick={() => handleDisapproveIVR(order.id, order.order_number)}
                                      disabled={
                                        approvingOrderId === order.id || 
                                        disapprovingOrderId === order.id ||
                                        !notesMap[order.id]?.trim()
                                      }
                                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-red-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-rose-600 hover:to-red-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                      {disapprovingOrderId === order.id ? (
                                        <>
                                          <motion.span
                                            animate={{ rotate: 360 }}
                                            transition={{
                                              duration: 1,
                                              repeat: Infinity,
                                              ease: "linear",
                                            }}
                                            className="inline-flex"
                                          >
                                            <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" />
                                          </motion.span>
                                          Disapproving...
                                        </>
                                      ) : (
                                        <>
                                          <IoAlertCircle className="text-lg" />
                                          Disapprove & Request Resubmission
                                        </>
                                      )}
                                    </button>
                                    
                                    {!notesMap[order.id]?.trim() && (
                                      <p className="text-xs text-amber-600 font-semibold text-center">
                                        Please enter medical notes to enable approval/disapproval
                                      </p>
                                    )}
                                  </div>
                                )}

                                {!order.ivr_download_url && (
                                  <p className="mt-3 text-xs font-semibold text-rose-600">
                                    IVR link missing — backend must provide ivr_download_url.
                                  </p>
                                )}
                              </>
                            ) : (
                              <div className="py-4 text-center">
                                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white ring-1 ring-slate-200">
                                  <IoAlertCircle className="text-2xl text-slate-400" />
                                </div>
                                <p className="mt-3 text-sm font-bold text-slate-800">
                                  No IVR Form
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                  Not submitted yet
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                        )
                      }
                     
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoBlock = ({ icon, label, value, subValue }) => {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
          <span className="text-lg">{icon}</span>
        </div>

        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-500">{label}</p>
          <p className="truncate text-sm font-bold text-slate-900">{value}</p>
          {subValue ? (
            <p className="truncate text-xs text-slate-500">{subValue}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PendingOrdersAdminDashboard;